/**
 * PageCoach — mounts a COACH sequence for the current page.
 *
 * The sequences in overlayCopy carry a CSS `anchor` selector instead of a React
 * ref, so a globally-mounted host (SetupDockMount) can run them without every
 * page threading refs down to its buttons. Anchors resolve live from the DOM on
 * each measure, so a sequence tolerates its target mounting a beat late.
 */

import React, { useMemo } from 'react';
import { CoachSequence, type CoachSequenceStep } from './CoachSequence';
import { COACH } from './overlayCopy';

interface Props {
  coachId: string;
  when?: boolean;
  onComplete?: () => void;
}

/** A ref-like object that re-queries its element from the DOM on every read. */
const selectorRef = (
  selector?: string
): { readonly current: HTMLElement | null } => ({
  get current() {
    return selector ? document.querySelector<HTMLElement>(selector) : null;
  },
});

export const PageCoach: React.FC<Props> = ({ coachId, when = true, onComplete }) => {
  const steps = useMemo<CoachSequenceStep[]>(
    () =>
      (COACH[coachId] ?? []).map((step) => ({
        ...step,
        anchorRef: selectorRef(step.anchor),
      })),
    [coachId]
  );

  if (steps.length === 0) return null;

  return (
    <CoachSequence
      id={coachId}
      steps={steps}
      when={when}
      onComplete={onComplete}
    />
  );
};

export default PageCoach;
